import { ref, onMounted } from 'vue';
import { useField } from 'vee-validate';
import { getYupValidationSchema } from '../validations-schema';
import flatpickr from 'flatpickr';

export default {
    props: {
        mode: {
            type: String,
            default: 'single', // single, range, or multiple
        },
        enableTime: {
            type: Boolean,
            default: false
        },
        time24hr: {
            type: Boolean,
            default: true
        },
        readonly: {
            type: Boolean,
            default: false
        },
        attributesString: {
            type: String,
            default: ''
        },
        autoComplete: {
            type: String,
            default: 'off'
        },
        description: {
            type: String,
            default: ''
        },
        label: {
            type: String,
            required: true
        },
        type: {
            type: String,
            default: 'datetime-local'
        },
        maxLength: {
            type: Number
        },
        name: {
            type: String,
            required: true
        },
        predefinedValue: {
            type: String,
            default: ''
        },
        clearButtonAriaLabel: {
            type: String,
            required: true
        },
        format: {
            type: String,
            default: 'd/m/Y'
        },
        minDate: {
            type: String
        },
        maxDate: {
            type: String
        },
        minTime: {
            type: String
        },
        maxTime: {
            type: String
        },
        required: {
            type: Boolean,
            default: false
        },
        validators: {
            type: Array
        },
        disable: {
            type: Array,
            default: []
        },
        value: {
            type: String
        },
        locale: {
            type: String,
            default: 'da'
        },
        extraValidationProps: {
            type: Object
        },
    },
    setup(props, { emit }) {
        let calendarInstance;
        const { value, errorMessage, handleBlur, handleChange } = useField(() => props.name,
            props.validators
                ? getYupValidationSchema(props.validators, props.extraValidationProps)
                : undefined, {
            initialValue: props.predefinedValue,
            syncVModel: true,
            validateOnValueUpdate: false,
        });

        // Validation listeners for the input field, active when validateOnValueUpdate is false.
        const validationListeners = {
            blur: evt => {
                const calendarContainer = calendarInstance?.calendarContainer;

                if (
                    !calendarContainer ||
                    !evt.relatedTarget ||
                    !evt.relatedTarget.closest('.flatpickr-calendar')
                ) {
                    handleBlur(evt, true);
                }
            },
            change: handleChange,
            input: evt => handleChange,
        };

        const attributes = ref(props.attributesString ? JSON.parse(props.attributesString) : {});
        const inputElement = ref(null);

        onMounted(async () => {
            await import(`flatpickr/dist/l10n/${props.locale}.js`);
            flatpickr(inputElement.value, {
                onReady: function (selectedDates, dateStr, instance) {
                    calendarInstance = instance;
                },
                onKeyDown: function (selectedDates, dateStr, instance, event) {
                    if (document.activeElement === inputElement.value) {
                        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                            moveFocusToCalendarIfArrowKeys(event, instance);
                        }
                    }
                },
                allowInput: !props.readonly,
                disableMobile: true,
                enableTime: props.enableTime,
                time_24hr: props.time_24hr,
                mode: props.mode,
                locale: props.locale,
                minDate: props.minDate,
                maxDate: props.maxDate,
                minTime: props.minTime,
                maxTime: props.maxTime,
                disable: props.disable,
                ...props.format ? { dateFormat: props.format } : {},
            })
        });

        const moveFocusToCalendarIfArrowKeys = (event, instance) => {
            instance.open();
            event.preventDefault();
            const daySelector = '.flatpickr-day';
            const selectedDay = instance.calendarContainer.querySelector(`${daySelector}.selected`);
            const currentDay = instance.calendarContainer.querySelector(`${daySelector}[aria-current]`);
            const firstVisibleDay = instance.calendarContainer.querySelector(daySelector);
            (selectedDay || currentDay || firstVisibleDay)?.focus();
        };

        const resetInputValue = () => {
            value.value = inputElement.value.value = '';
            inputElement.value.focus();
            emit('reset');
        };

        const onInputChange = (event) => {
            emit('input', value.value);
        };

        const onFocus = () => {
            emit('focus');
        };

        const onBlur = () => {
            emit('blur');
        };

        const onKeyDown = (event) => {
            // Prevent flat-pickr from bluring the input when pressing Enter or Space
            // and instead open the calendar popover.
            if (event.key === 'Enter' || event.key === " ") {
                event.preventDefault();
                event.stopImmediatePropagation();
                if (calendarInstance) {
                    calendarInstance.open();
                }
            }
        };

        return {
            attributes,
            errorMessage,
            value,
            validationListeners,
            inputElement,
            resetInputValue,
            onInputChange,
            onFocus,
            onBlur,
            onKeyDown
        };
    }
};